import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import { PageHero, CTABand, RequestCareForm } from "../components/Shared";
import { getService, SERVICES } from "../data/services";
import { Check, ArrowRight } from "lucide-react";

const BASE = "https://carefirstoh.com";

const List = ({ title, items }) => (
  <div>
    <h3 className="font-display text-[#16357a] text-[22px] font-semibold mb-4">{title}</h3>
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-[#444] text-[15px]"><span className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-[#4ca82f] flex items-center justify-center"><Check size={13} className="text-white" strokeWidth={3} /></span>{it}</li>
      ))}
    </ul>
  </div>
);

const ServiceDetail = () => {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <Navigate to="/services" replace />;
  const others = SERVICES.filter((x) => x.slug !== slug).slice(0, 4);

  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: s.name,
    description: s.seoDesc,
    provider: { "@type": "MedicalBusiness", name: "CareFirst Home Health Services LLC", areaServed: "Columbus, OH" },
  };

  return (
    <div>
      <Seo title={s.seoTitle} description={s.seoDesc} canonical={`${BASE}/services/${s.slug}`} jsonLd={schema} />
      <PageHero title={s.name} subtitle={s.intro} crumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: s.name }]} />

      <section className="bg-white py-16">
        <div className="max-w-[1150px] mx-auto px-4 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <div className="rounded-2xl overflow-hidden shadow-lg h-[320px]"><img src={s.image} alt={`${s.name} at home in Columbus, Ohio`} className="w-full h-full object-cover" /></div>
            <p className="text-[#4a4a4a] text-[17px] leading-relaxed">{s.intro}</p>
            <div className="grid sm:grid-cols-2 gap-10">
              <List title="Who Qualifies" items={s.whoQualifies} />
              <List title="Benefits" items={s.benefits} />
              <List title="Common Reasons" items={s.reasons} />
              <div>
                <h3 className="font-display text-[#16357a] text-[22px] font-semibold mb-4">What Families Can Expect</h3>
                <p className="text-[#444] text-[15px] leading-relaxed">{s.expectations}</p>
              </div>
            </div>
          </div>
          <aside className="lg:col-span-1">
            <div className="bg-[#eef6ea] rounded-2xl p-6 sticky top-28">
              <h3 className="font-display text-[#16357a] text-[22px] font-semibold mb-1">Request {s.name}</h3>
              <p className="text-[#666] text-[13px] mb-5">Tell us about your needs and we'll be in touch.</p>
              <RequestCareForm />
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-[#eef6ea] py-14">
        <div className="max-w-[1240px] mx-auto px-4">
          <h2 className="font-display text-[#16357a] text-[28px] font-semibold text-center mb-8">Explore More Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} className="bg-white rounded-xl p-5 shadow-sm hover:shadow-lg transition-all">
                <h3 className="font-semibold text-[#16357a] text-[16px] mb-1">{o.name}</h3>
                <span className="inline-flex items-center gap-1 text-[#4ca82f] text-[13px] font-semibold">View <ArrowRight size={13} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default ServiceDetail;
